import {
  add,
  addMinutes,
  differenceInMinutes,
  format,
  isAfter,
  isBefore,
  set,
  subMinutes,
} from "date-fns";

const FROM_CAEN = "fromCaen";
const TO_CAEN = "toCaen";

const STOPS = {
  HAUT_LION: "Haut Lion",
  BAS_LION: "Bas Lion",
  LION_STADE: "Lion Stade",
  SNCF: "Gare SNCF",
  OUISTREAM_PORT: "Ouistream Port",
  TOUR_LEROY: "Tour Leroy",
  CALVAIRE: "Calvaire St-Pierre",
};
const TYPES = {
  BUS12: "Bus 12",
  BUS22: "Bus 22",
  BUS122: "Bus 122",
  TRAM1: "Tram 1",
  TRAM2: "Tram 2",
  TRAM3: "Tram 3",
  WALK: "Marche",
};

const SNCF_LEROY_WALK_MINUTES = 17;
const MARGIN_TRAM_CHANGE_MINUTES = 5;

// A stop can be given either as an id, or as a list of ids: in that case we keep
// the first one the line actually serves. Twisto sometimes replaces a stop by
// another one (in summer the 12 skips Haut Lion and stops at Bas Lion instead),
// so the alternatives let us follow the line wherever it goes.
const findStopToKeep = (timetable, stop) => {
  const found = (Array.isArray(stop) ? stop : [stop])
    .map((id) => timetable.stops.findIndex(({ id: stopId }) => stopId === id))
    .filter((index) => index !== -1);

  // A line can keep a stop in its timetable without stopping there anymore: the
  // 22 kept Haut Lion with an empty column when it stopped serving it. So an
  // alternative where the buses really pass wins over one that is only listed.
  const index = found.find((i) =>
    timetable.trips.some(({ times }) => times[i])
  );

  if (index === undefined) {
    if (found.length) {
      // Nothing runs there that day (a Sunday, or a day Twisto does not publish
      // yet): keep the stop anyway, there is simply no trip to show.
      return { stop: timetable.stops[found[0]], index: found[0] };
    }

    console.warn("None of these stops is served by the line", stop);
    return null;
  }

  return { stop: timetable.stops[index], index };
};

const getTwistoTimes = async (line, direction, date, stops) => {
  const res = await fetch(
    `https://twisto.lesbriques.co/timetable/${line}?direction=${direction}&date=${format(
      date,
      "yyyy-MM-dd"
    )}`
  );
  const timetable = await res.json();
  const foundStops = stops.map((stop) => findStopToKeep(timetable, stop));

  if (foundStops.some((stopToKeep) => !stopToKeep)) {
    // Callers read the times by position, so a missing stop would shift every
    // other one and make them read the times of the wrong stop. Better show no
    // trip at all than made up ones.
    return {
      stops: foundStops.filter((s) => !!s).map(({ stop }) => stop),
      trips: [],
    };
  }

  // Keep the stops in the order the line serves them, as the trips are read
  // from the first stop of the journey to the last one.
  const stopsToKeep = foundStops.sort((a, b) => a.index - b.index);

  const trips = timetable.trips
    .map((trip) => {
      const times = stopsToKeep.map(({ index }) => trip.times[index]);
      return {
        ...trip,
        times,
      };
    })
    .filter((trip) => trip.times.some((time) => time !== null));

  return { stops: stopsToKeep.map(({ stop }) => stop), trips };
};

// Where we can get on and off in Lion, from the closest to Les Briques to the
// furthest one we accept to walk to. A line going through none of them cannot
// bring us home. Twisto changes the one a line serves from a season to the next,
// but a given line serves the same one on all of its trips.
const LION_STOPS = {
  OUTWARD: ["TWISTO:lmhl01", "TWISTO:list01", "TWISTO:lmbl01"],
  RETURN: ["TWISTO:lmhl02", "TWISTO:list02", "TWISTO:lmbl02"],
};

// The name to display for the Lion stop a line actually serves that day.
const LION_STOP_NAMES = {
  "TWISTO:lmhl01": STOPS.HAUT_LION,
  "TWISTO:lmhl02": STOPS.HAUT_LION,
  "TWISTO:lmbl01": STOPS.BAS_LION,
  "TWISTO:lmbl02": STOPS.BAS_LION,
  "TWISTO:list01": STOPS.LION_STADE,
  "TWISTO:list02": STOPS.LION_STADE,
};

const findLionStopName = (stops) => {
  const lionStop = stops.find(({ id }) => LION_STOP_NAMES[id]);
  return lionStop ? LION_STOP_NAMES[lionStop.id] : STOPS.HAUT_LION;
};

const getBus12TwistoTimes = async (direction, date) => {
  const stops = [
    LION_STOPS[direction],
    direction === "RETURN" ? "TWISTO:poou02" : "TWISTO:poou01",
    direction === "RETURN" ? "TWISTO:tl.1" : "TWISTO:tl.2",
    direction === "RETURN"
      ? ["TWISTO:gasn11", "TWISTO:gasn1"]
      : ["TWISTO:gasn12", "TWISTO:gasn2"],
  ];
  const bus12 = await getTwistoTimes("12", direction, date, stops);

  return {
    stops: bus12.stops,
    trips: [...bus12.trips],
    lionStopName: findLionStopName(bus12.stops),
  };
};

const parseTrips = (trips, date) => {
  const dateAt4 = set(date, { hours: 4, minutes: 0, seconds: 0 });
  return trips.map((trip) => {
    const times = trip.times.map((time) => {
      if (time === null) {
        return null;
      }
      const [hours, minutes] = time.split(":").map((t) => parseInt(t, 10));
      return add(dateAt4, { hours: hours - 4 + (hours < 4 ? 24 : 0), minutes });
    });

    return {
      ...trip,
      times,
    };
  });
};

const findSNCFLionTripsWithBus12 = async (direction, date) => {
  const { trips, lionStopName } = await getBus12TwistoTimes(
    direction === FROM_CAEN ? "RETURN" : "OUTWARD",
    date
  );
  const bus12 = parseTrips(trips, date);

  const tripsBus12 = bus12
    .map(({ times }) => {
      if (times[0] && times[3]) {
        // The bus goes from Lion to Gare SNCF (or the other way)
        const durationInMinutes = differenceInMinutes(times[3], times[0]);
        return {
          departure: times[0],
          arrival: times[3],
          durationInMinutes,
          legs: [
            {
              departure: times[0],
              arrival: times[3],
              durationInMinutes,
              origin: direction === TO_CAEN ? lionStopName : STOPS.SNCF,
              destination: direction === TO_CAEN ? STOPS.SNCF : lionStopName,
              type: TYPES.BUS12,
            },
          ],
        };
      } else if (times[2]) {
        // The bus stops at Tour Leroy but does not go to Gare SNCF
        if (direction === TO_CAEN) {
          if (!times[0]) {
            return null;
          }

          const busDurationInMinutes = differenceInMinutes(times[2], times[0]);
          const arrival = addMinutes(times[2], SNCF_LEROY_WALK_MINUTES);
          return {
            departure: times[0],
            arrival,
            durationInMinutes: busDurationInMinutes + SNCF_LEROY_WALK_MINUTES,
            legs: [
              {
                departure: times[0],
                arrival: times[2],
                durationInMinutes: busDurationInMinutes,
                origin: lionStopName,
                destination: STOPS.TOUR_LEROY,
                type: TYPES.BUS12,
              },
              {
                departure: times[2],
                arrival,
                durationInMinutes: SNCF_LEROY_WALK_MINUTES,
                origin: STOPS.TOUR_LEROY,
                destination: STOPS.SNCF,
                type: TYPES.WALK,
              },
            ],
          };
        } else if (times[1] && times[3]) {
          const busDurationInMinutes = differenceInMinutes(times[3], times[1]);
          const departure = subMinutes(times[1], SNCF_LEROY_WALK_MINUTES);
          return {
            departure,
            arrival: times[3],
            durationInMinutes: busDurationInMinutes + SNCF_LEROY_WALK_MINUTES,
            legs: [
              {
                departure,
                arrival: times[1],
                durationInMinutes: SNCF_LEROY_WALK_MINUTES,
                origin: STOPS.SNCF,
                destination: STOPS.TOUR_LEROY,
                type: TYPES.WALK,
              },
              {
                departure: times[1],
                arrival: times[3],
                durationInMinutes: busDurationInMinutes,
                origin: STOPS.TOUR_LEROY,
                destination: lionStopName,
                type: TYPES.BUS12,
              },
            ],
          };
        }
      } else {
        // It can happen the bus only go to Ouistream, in that case
        // we skip it.
        return;
      }
    })
    .filter((t) => !!t);

  return tripsBus12;
};

const findClosestTramGoingToCalvaire = (
  { tram1, tram2 },
  departureFromCalvaire
) => {
  const tram1Before = tram1.filter(({ times }) =>
    isBefore(times[1], departureFromCalvaire)
  );
  const tram2Before = tram2.filter(({ times }) =>
    isBefore(times[1], departureFromCalvaire)
  );
  const bestTram1 = tram1Before[tram1Before.length - 1];
  const bestTram2 = tram2Before[tram2Before.length - 1];
  if (bestTram1) {
    bestTram1.type = TYPES.TRAM1;
  }
  if (bestTram2) {
    bestTram2.type = TYPES.TRAM2;
  }
  if (bestTram1 && bestTram2) {
    return isAfter(bestTram1.times[0], bestTram2.times[0])
      ? bestTram1
      : bestTram2;
  } else if (bestTram1) {
    return bestTram1;
  } else if (bestTram2) {
    return bestTram2;
  }
};

const findClosestTramLeavingCalvaire = (
  { tram1, tram2 },
  arrivalToCalvaire
) => {
  const tram1After = tram1.filter(({ times }) =>
    isAfter(times[0], arrivalToCalvaire)
  );
  const tram2After = tram2.filter(({ times }) =>
    isAfter(times[0], arrivalToCalvaire)
  );
  const bestTram1 = tram1After[0];
  const bestTram2 = tram2After[0];
  if (bestTram1) {
    bestTram1.type = TYPES.TRAM1;
  }
  if (bestTram2) {
    bestTram2.type = TYPES.TRAM2;
  }
  if (bestTram1 && bestTram2) {
    return isBefore(bestTram1.times[0], bestTram2.times[0])
      ? bestTram1
      : bestTram2;
  } else if (bestTram1) {
    return bestTram1;
  } else if (bestTram2) {
    return bestTram2;
  }
};

// The buses going from Lion to Calvaire St-Pierre, where a tram takes over to
// reach Gare SNCF.
const CALVAIRE_STOP = "TWISTO:casp41";
const CALVAIRE_BUSES = [
  { line: "22", type: TYPES.BUS22 },
  // The 122 only runs on school days.
  { line: "122", type: TYPES.BUS122 },
];

// The trams are the same whichever bus goes to Calvaire, so they are only
// retrieved once and shared by all of them.
const getTramsToCalvaire = async (direction, date) => {
  const twistoDirection = direction === FROM_CAEN ? "RETURN" : "OUTWARD";
  const [tram1Times, tram2Times] = await Promise.all([
    getTwistoTimes("T1", twistoDirection, date, [
      direction === FROM_CAEN ? "TWISTO:gs02" : "TWISTO:gs01",
      direction === FROM_CAEN ? "TWISTO:csp02" : "TWISTO:csp01",
    ]),
    getTwistoTimes("T2", twistoDirection, date, [
      direction === FROM_CAEN ? "TWISTO:rdo02" : "TWISTO:rdo01",
      direction === FROM_CAEN ? "TWISTO:csp02" : "TWISTO:csp01",
    ]),
  ]);

  return {
    tram1: parseTrips(tram1Times.trips, date),
    tram2: parseTrips(tram2Times.trips, date),
  };
};

const findSNCFLionTripsViaCalvaire = async (bus, direction, date, trams) => {
  const twistoDirection = direction === FROM_CAEN ? "RETURN" : "OUTWARD";
  const busTimes = await getTwistoTimes(bus.line, twistoDirection, date, [
    LION_STOPS[twistoDirection],
    CALVAIRE_STOP,
  ]);

  const busTrips = parseTrips(busTimes.trips, date);
  const lionStopName = findLionStopName(busTimes.stops);
  const { tram1, tram2 } = trams;

  if (direction === FROM_CAEN) {
    return busTrips
      .filter(({ times }) => times[1])
      .map(({ times, onDemand }) => {
        const arrival = times[1];

        // Departure from Calvaire
        const bestTram = findClosestTramGoingToCalvaire(
          { tram1, tram2 },
          subMinutes(times[0], MARGIN_TRAM_CHANGE_MINUTES)
        );
        if (bestTram) {
          const departure = bestTram.times[0];
          const durationInMinutes = differenceInMinutes(arrival, departure);

          return {
            departure,
            arrival,
            durationInMinutes,
            legs: [
              {
                departure,
                arrival: bestTram.times[1],
                durationInMinutes: differenceInMinutes(
                  bestTram.times[1],
                  bestTram.times[0]
                ),
                origin: STOPS.SNCF,
                destination: STOPS.CALVAIRE,
                type: bestTram.type,
              },
              {
                departure: times[0],
                arrival,
                durationInMinutes,
                origin: STOPS.CALVAIRE,
                destination: lionStopName,
                type: bus.type,
              },
            ],
            onDemand,
          };
        }
      });
  } else {
    return busTrips
      .filter(({ times }) => times[0])
      .map(({ times, onDemand }) => {
        const departure = times[0];

        const bestTram = findClosestTramLeavingCalvaire(
          { tram1, tram2 },
          addMinutes(times[1], MARGIN_TRAM_CHANGE_MINUTES)
        );
        if (bestTram) {
          const arrival = bestTram.times[1];
          const durationInMinutes = differenceInMinutes(arrival, departure);

          return {
            departure,
            arrival,
            durationInMinutes,
            legs: [
              {
                departure,
                arrival: times[1],
                durationInMinutes: differenceInMinutes(times[1], times[0]),
                origin: lionStopName,
                destination: STOPS.CALVAIRE,
                type: bus.type,
              },
              {
                departure: bestTram.times[0],
                arrival,
                durationInMinutes: differenceInMinutes(
                  bestTram.times[1],
                  bestTram.times[0]
                ),
                origin: STOPS.CALVAIRE,
                destination: STOPS.SNCF,
                type: bestTram.type,
              },
            ],
            onDemand,
          };
        }
      });
  }
};

const findSNCFLionTrips = async (direction, date) => {
  const trams = await getTramsToCalvaire(direction, date);
  const tripsByLine = await Promise.all([
    findSNCFLionTripsWithBus12(direction, date),
    ...CALVAIRE_BUSES.map((bus) =>
      findSNCFLionTripsViaCalvaire(bus, direction, date, trams)
    ),
  ]);

  const trips = tripsByLine
    .flat()
    .filter((t) => !!t)
    .sort((a, b) => (isBefore(a.departure, b.departure) ? -1 : 1));

  return trips;
};

const findOuistreamLionTrips = async (direction, date) => {
  const { trips, lionStopName } = await getBus12TwistoTimes(
    direction === FROM_CAEN ? "OUTWARD" : "RETURN",
    date
  );
  const bus12 = parseTrips(trips, date);

  if (direction === TO_CAEN) {
    return bus12
      .filter((trip) => trip.times[0] && trip.times[1])
      .map((trip) => {
        const departure = trip.times[0];
        const arrival = trip.times[1];
        const durationInMinutes = differenceInMinutes(arrival, departure);

        return {
          departure,
          arrival,
          durationInMinutes,
          legs: [
            {
              departure,
              arrival,
              durationInMinutes,
              origin: lionStopName,
              destination: STOPS.OUISTREAM,
              type: TYPES.BUS12,
            },
          ],
        };
      });
  }
  if (direction === FROM_CAEN) {
    return bus12
      .filter((trip) => trip.times[2] && trip.times[3])
      .map((trip) => {
        const departure = trip.times[2];
        const arrival = trip.times[3];
        const durationInMinutes = differenceInMinutes(arrival, departure);

        return {
          departure,
          arrival,
          durationInMinutes,
          legs: [
            {
              departure,
              arrival,
              durationInMinutes,
              origin: STOPS.OUISTREAM,
              destination: lionStopName,
              type: TYPES.BUS12,
            },
          ],
        };
      });
  }
};

const displayTrips = (trips) => {
  console.log(trips);
  let html = "";
  for (const trip of trips) {
    // Only the buses going through Calvaire can be on demand, and the trip has
    // to be booked two hours before that bus leaves.
    const onDemandLeg = trip.onDemand
      ? trip.legs.find(({ type }) =>
          CALVAIRE_BUSES.some((bus) => bus.type === type)
        )
      : null;
    html += `
    <div class="Trip">
      <div class="Trip-Row">
        <div class="Trip-Stop">
          <div class="Trip-Stop-Time">${format(trip.departure, "HH:mm")}</div>
          <div class="Trip-Stop-Place">${trip.legs[0].origin}</div>
        </div>
        <div class="Trip-Transport">
          <div class="Trip-Transport-Icon">→</div>
          <div class="Trip-Transport-Type">${trip.legs[0].type}</div>
        </div>
        ${
          trip.legs.length > 1
            ? `<div class="Trip-Stop Middle">
          <div class="Trip-Stop-Time">${format(
            trip.legs[1].departure,
            "HH:mm"
          )}</div>
          <div class="Trip-Stop-Place">${trip.legs[1].origin}</div>
        </div>
        <div class="Trip-Transport">
          <div class="Trip-Transport-Icon">→</div>
          <div class="Trip-Transport-Type">${trip.legs[1].type}</div>
        </div>`
            : ""
        }
        <div class="Trip-Stop Arrival">
          <div class="Trip-Stop-Time">${format(trip.arrival, "HH:mm")}</div>
          <div class="Trip-Stop-Place">${
            trip.legs[trip.legs.length - 1].destination
          }</div>
        </div>
      </div>
      ${
        onDemandLeg
          ? `<div class="Trip-OnDemand">Ce ${onDemandLeg.type.toLowerCase()} doit être réservé au <a href="tel:+33231155555">02 31 15 55 55</a> avant ${format(
              subMinutes(onDemandLeg.departure, 120),
              "HH:mm"
            )}.</div>`
          : ""
      }
    </div>
      `;
  }
  document.getElementById("results").innerHTML = html;
};

const findAndDisplayTrips = async (trip, date) => {
  const trips =
    trip === "lion-caen"
      ? await findSNCFLionTrips(TO_CAEN, date)
      : await findSNCFLionTrips(FROM_CAEN, date);

  displayTrips(trips);
};

document.getElementById("date").valueAsDate = new Date();

const onFormChange = () => {
  const trip = document.querySelector("input[name=trip]:checked").value;
  const date = document.getElementById("date").valueAsDate;
  findAndDisplayTrips(trip, date);
};

document.querySelectorAll("input[name=trip]").forEach((input) => {
  input.addEventListener("change", onFormChange);
});
document.getElementById("date").addEventListener("change", onFormChange);

onFormChange();
